import './styles/CodeBlock.scss';
import './styles/JSONCodeBlock.scss';

import { Eye, EyeSlash } from '@phosphor-icons/react';
import { ReactNode, useId, useState } from 'react';

import { useClassHelper } from '../../hooks';
import { CopyButton } from '../CopyButton';
import { InputField } from '../InputField';
import { Stack } from '../Stack';
import { formatXML, prettyPrint } from './helpers';

interface SharedProps {
  label?: ReactNode;
  id?: string;
  /**
   * Where the copy / reveal actions sit. The `overlay-*` variants float them
   * inside the right edge of the code, vertically anchored top / middle /
   * bottom. Plain `overlay` (default) is an alias of `overlay-middle`.
   * `top` puts them in a toolbar row above the code so they never cover
   * scrolling text. `masked` always uses `top`, since a masked value must
   * stay readable when revealed.
   */
  actionsPosition?:
    | 'overlay'
    | 'overlay-top'
    | 'overlay-middle'
    | 'overlay-bottom'
    | 'top';
  /**
   * Caps the code area height and scrolls it vertically — for tall content
   * like PEM blocks or logs. Numbers are px; strings pass through verbatim.
   * The floating actions stay anchored to the block, not the scrolled text.
   */
  maxHeight?: number | string;
  className?: string;
  testId?: string;
}

interface TextProps extends SharedProps {
  /** Array of code lines to display verbatim. Mutually exclusive with `obj`. */
  value: string[];
  obj?: never;
  masked?: boolean;
  /**
   * Hides the line-number gutter — for single-line values (a URL, a token)
   * or content where line numbers are noise.
   */
  hideLineNumbers?: boolean;
  variant?: never;
  errorAttributes?: never;
}

interface JsonProps extends SharedProps {
  /**
   * JSON mode: an object renders pretty-printed with syntax highlighting; a
   * string renders XML-formatted. Mutually exclusive with `value`.
   */
  obj: Record<string, any> | string;
  value?: never;
  variant?: 'default' | 'danger';
  /** Object keys highlighted with the error color (validation errors). */
  errorAttributes?: string[];
  masked?: never;
  hideLineNumbers?: never;
}

export type Props = TextProps | JsonProps;

const componentName = 'c-CopyButtonWrapper';
const jsonComponentName = 'c-JSONCodeBlock';

// Discriminate on the absence of `value`, not the presence of `obj`: legacy
// JSONCodeBlock call sites pass a possibly-undefined obj (e.g. a response
// body that did not arrive) and expect the block to render nothing.
const isJsonProps = (props: Props): props is JsonProps => {
  return props.value === undefined;
};

const JsonCodeContent = ({
  obj,
  variant = 'default',
  errorAttributes = [],
  className = '',
  testId,
}: JsonProps) => {
  const c = useClassHelper(jsonComponentName);

  if (!obj) {
    return null;
  }

  const variantClassName =
    variant === 'danger' ? c('__danger') : c('__default');

  const codeBlock = (
    <div className={`${jsonComponentName} ${variantClassName} ${className}`}>
      <div className={c('__wrapper')}>
        {typeof obj === 'string' ? (
          <pre data-testid={testId}>{formatXML(obj)}</pre>
        ) : (
          <pre
            data-testid={testId}
            dangerouslySetInnerHTML={prettyPrint(obj, errorAttributes)}
          />
        )}
        <div className={c('__white-space')} aria-hidden="true" />
      </div>
    </div>
  );

  return (
    <CopyButton.Wrapper
      value={typeof obj === 'string' ? formatXML(obj) : obj}
      variant="top-right"
    >
      {codeBlock}
    </CopyButton.Wrapper>
  );
};

export const CodeBlock = (props: Props) => {
  const generatedId = useId();
  const [revealed, setRevealed] = useState(false);
  const c = useClassHelper(componentName);

  if (isJsonProps(props)) {
    return <JsonCodeContent {...props} />;
  }

  const {
    value,
    label,
    id: providedId,
    masked = false,
    actionsPosition = 'overlay',
    maxHeight,
    hideLineNumbers = false,
    className = '',
    testId,
  } = props;

  const codeBlockId = providedId || generatedId;
  const labelId = `${codeBlockId}-label`;

  if (value.length === 0) {
    throw new Error('CopyButtonWrapper: value prop is required');
  }

  const isOneLine = value.length === 1;
  const isHidden = masked && !revealed;
  const isTop = masked || actionsPosition === 'top';
  // Plain 'overlay' keeps its historical behaviour, which was always
  // vertically centered — it is an alias of 'overlay-middle'.
  const overlayVariant =
    actionsPosition === 'overlay' ? 'overlay-middle' : actionsPosition;

  const getDisplayValue = (item: string) => {
    if (isHidden) {
      return item.replace(/./g, '•');
    }

    return item;
  };

  const labelElement = label ? (
    <InputField.Label id={labelId} className={c('__field-label')}>
      {label}
    </InputField.Label>
  ) : null;

  const maskToggle = masked ? (
    <button
      type="button"
      className={c('__toggle-mask', { '__toggle-mask--inline': isTop })}
      onClick={() => setRevealed(prev => !prev)}
      aria-pressed={revealed}
      aria-label={revealed ? 'Hide value' : 'Show value'}
    >
      {revealed ? (
        <EyeSlash size={20} aria-label="hide value icon" />
      ) : (
        <Eye size={20} aria-label="show value icon" />
      )}
    </button>
  ) : null;

  const contentStyle =
    maxHeight !== undefined
      ? {
          maxHeight:
            typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
          overflowY: 'auto' as const,
        }
      : undefined;

  const content = (
    <div className={c('__content-wrapper')} style={contentStyle}>
      {value.map((item, index) => {
        const lineNumber = index + 1;
        return (
          <code
            className={c('__code-wrapper', {
              '__code-wrapper--no-line-numbers': hideLineNumbers,
            })}
            key={`copy-button-${index}`}
          >
            {!hideLineNumbers && (
              <span
                className={c('__line-number', {
                  '__line-number--one-line': isOneLine,
                })}
                data-number={lineNumber}
                aria-hidden="true"
              ></span>
            )}
            <pre
              className={c('__code', {
                '__code--one-line': isOneLine,
              })}
            >
              {getDisplayValue(item)}
            </pre>
            {/* Magic tag because padding doesnt work */}
            <span className={c('__white-space')} aria-hidden="true" />
          </code>
        );
      })}
    </div>
  );

  if (isTop) {
    return (
      <Stack direction="column">
        <div
          className={`${c('base', '--top')} ${className}`.trim()}
          id={codeBlockId}
          aria-labelledby={label ? labelId : undefined}
          role="region"
          data-testid={testId}
        >
          <div className={c('__toolbar')}>
            {labelElement}
            <div className={c('__actions')}>
              {maskToggle}
              <CopyButton value={value} variant="row-header" />
            </div>
          </div>
          {content}
        </div>
      </Stack>
    );
  }

  return (
    <Stack direction="column">
      {labelElement}
      <div
        className={`${componentName} ${c(
          `--${overlayVariant}`,
        )} ${className}`.trim()}
        id={codeBlockId}
        aria-labelledby={label ? labelId : undefined}
        role="region"
        data-testid={testId}
      >
        {content}
        {maskToggle}
        <CopyButton value={value} />
      </div>
    </Stack>
  );
};
